import {
  Activity,
  HeartPulse,
  Stethoscope,
  Baby,
  Smile,
  Eye,
  Scissors,
  Scale,
} from 'lucide-react'

export const procedures = [
  {
    slug: 'dental',
    name: 'Dental Care',
    icon: Activity,
    shortBlurb: 'Implants, veneers, crowns, and full mouth restorations.',
    heroImage:
      'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?q=80&w=2940&auto=format&fit=crop',
    illustrativePriceFrom: '$800',
    illustrativeSavingsPct: '70%',
    destinations: ['Mexico', 'Costa Rica', 'Turkey', 'Hungary'],
    averageProcedureTime: '2 to 4 hours per session',
    stayRequired: '5 to 10 days (may require two trips for implants)',
    insuranceDetails:
      'Often not covered by domestic health insurance. Some international dental plans may offer partial reimbursement. Financing options are frequently utilized.',
    risks: [
      'Infection at the implant site',
      'Nerve damage causing pain or numbness',
      'Implant failure or rejection',
      'Sinus problems for upper jaw implants',
    ],
    priceComparison: [
      { destination: 'United States', price: '$4,000 - $6,000 (per implant)' },
      { destination: 'Mexico', price: '$800 - $1,200 (per implant)' },
      { destination: 'Costa Rica', price: '$900 - $1,500 (per implant)' },
      { destination: 'Turkey', price: '$600 - $1,000 (per implant)' },
      { destination: 'Hungary', price: '$700 - $1,100 (per implant)' },
    ],
    content: `
      Dental care abroad represents one of the most established and reliable segments of medical travel. Patients seeking complex restorative work, such as full mouth reconstructions, All-on-4 implants, or extensive cosmetic veneers, often face prohibitive costs in their home countries. By traveling to accredited international clinics, patients can access state-of-the-art technology, including 3D CBCT scanning and CAD/CAM milling, at a fraction of the domestic price.

      The primary beneficiaries of international dental care are individuals requiring multiple implants, extensive crown work, or full arch restorations. For a single filling, travel is rarely justified. However, when treatment plans exceed several thousand dollars, the economics shift dramatically. Patients can often cover the cost of flights, premium accommodations, and the procedure itself while still realizing significant savings. 

      When you arrive at a vetted destination clinic, you can expect a highly streamlined experience. Many international dental centers cater specifically to traveling patients, offering expedited lab work that condenses months of appointments into a single week. The clinics we coordinate with employ specialists who have often trained or completed fellowships in the United States or Europe, ensuring familiarity with the latest clinical protocols and materials.
    `,
  },
  {
    slug: 'bariatric',
    name: 'Bariatric Surgery',
    icon: Scale,
    shortBlurb: 'Gastric sleeve, gastric bypass, and revision surgeries.',
    heroImage:
      'https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=2940&auto=format&fit=crop',
    illustrativePriceFrom: '$4,500',
    illustrativeSavingsPct: '65%',
    destinations: ['Mexico', 'Turkey', 'Thailand'],
    averageProcedureTime: '1 to 3 hours',
    stayRequired: '7 to 10 days (including 2-3 nights in hospital)',
    insuranceDetails:
      'Domestic insurance often requires lengthy waiting periods, strict BMI criteria, and supervised diets. Traveling allows patients to bypass these hurdles and self-fund at a fraction of the cost.',
    risks: [
      'Bleeding or leaking from the staple line',
      'Infection or blood clots',
      'Nutritional deficiencies post-surgery',
      'Acid reflux or strictures',
    ],
    priceComparison: [
      { destination: 'United States', price: '$15,000 - $25,000' },
      { destination: 'Mexico', price: '$4,500 - $6,500' },
      { destination: 'Turkey', price: '$3,500 - $5,500' },
      { destination: 'Thailand', price: '$9,000 - $12,000' },
    ],
    content: `
      Bariatric surgery, including the highly popular gastric sleeve and gastric bypass procedures, is a life-changing intervention for individuals struggling with obesity. International centers of excellence have performed tens of thousands of these procedures, refining their protocols to ensure maximum safety and rapid recovery.

      Many patients choose to travel for bariatric surgery because domestic health insurance policies often impose restrictive criteria, such as mandatory six-month supervised diets or extremely high BMI thresholds. By choosing an accredited international facility, patients can take control of their health timeline while saving significantly on out-of-pocket costs.

      The international bariatric journey includes comprehensive pre-operative evaluations, the surgery itself performed laparoscopically, and dedicated post-operative care. Facilities often provide specialized nutritional counseling and long-term virtual follow-up to ensure patients adapt successfully to their new lifestyle and dietary requirements.
    `,
  },
  {
    slug: 'cosmetic',
    name: 'Cosmetic Surgery',
    icon: Smile,
    shortBlurb:
      'Rhinoplasty, breast augmentation, facelifts, and body contouring.',
    heroImage:
      'https://images.unsplash.com/photo-1512678080530-7760d81faba6?q=80&w=2944&auto=format&fit=crop',
    illustrativePriceFrom: '$3,500',
    illustrativeSavingsPct: '60%',
    destinations: ['Turkey', 'Thailand', 'Mexico', 'Brazil'],
    averageProcedureTime: '2 to 6 hours depending on the procedure',
    stayRequired: '7 to 14 days for initial recovery and follow-up',
    insuranceDetails:
      'Elective cosmetic procedures are strictly out-of-pocket and not covered by standard health insurance. Medical travel insurance is highly recommended to cover unforeseen travel or medical complications.',
    risks: [
      'Adverse reaction to anesthesia',
      'Infection or poor wound healing',
      'Scarring or asymmetry',
      'Deep vein thrombosis (DVT) related to travel post-surgery',
    ],
    priceComparison: [
      { destination: 'United States', price: '$8,000 - $15,000 (Rhinoplasty)' },
      { destination: 'Turkey', price: '$2,500 - $4,500 (Rhinoplasty)' },
      { destination: 'Mexico', price: '$3,500 - $5,500 (Rhinoplasty)' },
      { destination: 'Thailand', price: '$4,000 - $7,000 (Rhinoplasty)' },
    ],
    content: `
      Cosmetic and plastic surgery is a leading driver of medical travel, offering patients access to highly skilled surgeons and modern facilities at accessible price points. Procedures ranging from rhinoplasty and breast augmentation to comprehensive body contouring and facial rejuvenation are routinely performed by international specialists who dedicate their practices entirely to aesthetic medicine.

      This option is ideal for individuals seeking elective procedures that are not covered by standard health insurance. The high out-of-pocket costs in domestic markets often put these transformative surgeries out of reach. By exploring international options, patients can achieve their aesthetic goals without compromising on safety or surgical expertise. Furthermore, the privacy afforded by recovering away from home is a significant draw for many.

      The patient journey typically begins with a detailed virtual consultation, allowing the surgeon to assess your anatomy and discuss realistic outcomes before you travel. Upon arrival, you will undergo comprehensive pre-operative testing to ensure you are a safe candidate for anesthesia. The recovery period is carefully managed, often in specialized recovery retreats or premium hotels that partner with the clinics to provide nursing support and post-operative monitoring.
    `,
  },
  {
    slug: 'orthopedic',
    name: 'Orthopedic Surgery',
    icon: Stethoscope,
    shortBlurb: 'Knee replacement, hip replacement, and spinal surgeries.',
    heroImage:
      'https://images.unsplash.com/photo-1579684385127-1ef15d508118?q=80&w=2960&auto=format&fit=crop',
    illustrativePriceFrom: '$8,500',
    illustrativeSavingsPct: '75%',
    destinations: ['India', 'Mexico', 'Thailand', 'Spain'],
    averageProcedureTime: '2 to 4 hours',
    stayRequired: '14 to 21 days including intensive physical therapy',
    insuranceDetails:
      'Some self-funded corporate insurance plans or specialized medical travel networks may offer coverage. Otherwise, it is an out-of-pocket expense. Patients often use HSA/FSA funds where applicable.',
    risks: [
      'Blood clots (DVT or pulmonary embolism)',
      'Joint infection requiring revision surgery',
      'Nerve or blood vessel damage',
      'Implant loosening or wear over time',
    ],
    priceComparison: [
      {
        destination: 'United States',
        price: '$35,000 - $50,000 (Knee Replacement)',
      },
      { destination: 'India', price: '$6,000 - $8,500 (Knee Replacement)' },
      { destination: 'Mexico', price: '$10,000 - $14,000 (Knee Replacement)' },
      {
        destination: 'Thailand',
        price: '$12,000 - $16,000 (Knee Replacement)',
      },
    ],
    content: `
      Orthopedic surgery, including major joint replacements and spinal interventions, requires a high level of clinical precision and dedicated post-operative rehabilitation. International hospitals have invested heavily in advanced orthopedic technology, such as robotic-assisted surgery systems and minimally invasive techniques, allowing them to deliver outcomes that rival or exceed those of domestic institutions.

      Patients suffering from chronic joint pain, osteoarthritis, or debilitating spinal conditions often face long wait times or insurmountable deductibles in their home countries. Medical travel provides a viable pathway to regain mobility and improve quality of life promptly. The comprehensive care packages available internationally often include not only the surgery but also extensive inpatient physical therapy, which is crucial for a successful recovery.

      When traveling for orthopedic care, the process is meticulously planned to minimize physical strain. Upon arrival, patients are typically admitted directly to the hospital for pre-operative assessments. Following the procedure, the initial recovery takes place in the hospital under close medical supervision. Once cleared, patients transition to a comfortable hotel or specialized recovery center where physical therapy continues daily until they are fit to fly home.
    `,
  },
  {
    slug: 'hair-transplant',
    name: 'Hair Transplantation',
    icon: Scissors,
    shortBlurb: 'FUE, DHI, and comprehensive hair restoration treatments.',
    heroImage:
      'https://images.unsplash.com/photo-1629909613654-28e377c37b09?q=80&w=2936&auto=format&fit=crop',
    illustrativePriceFrom: '$2,000',
    illustrativeSavingsPct: '80%',
    destinations: ['Turkey', 'Mexico', 'India'],
    averageProcedureTime: '6 to 8 hours (single session)',
    stayRequired: '3 to 5 days',
    insuranceDetails:
      'Considered a cosmetic procedure and is not covered by health insurance. Patients self-fund this treatment.',
    risks: [
      'Infection or folliculitis',
      'Shock loss (temporary shedding of existing hair)',
      'Unnatural looking hairline if performed by inexperienced technicians',
      'Scarring in the donor area',
    ],
    priceComparison: [
      { destination: 'United States', price: '$10,000 - $15,000' },
      { destination: 'Turkey', price: '$2,000 - $3,500' },
      { destination: 'Mexico', price: '$3,000 - $5,000' },
      { destination: 'India', price: '$1,500 - $3,000' },
    ],
    content: `
      Hair transplantation has seen a massive surge in international popularity, largely driven by the pioneering techniques and high-volume experience of clinics abroad. Modern methods like Follicular Unit Extraction (FUE) and Direct Hair Implantation (DHI) leave minimal scarring and offer natural-looking, dense results.

      The cost of hair restoration in Western countries is often calculated per graft, making extensive procedures incredibly expensive. International clinics typically offer all-inclusive packages that cover the maximum number of grafts safely extractable in a single session, along with VIP transfers and luxury hotel stays, for a fraction of the domestic price.

      While the procedure is minimally invasive, it requires significant artistic skill and technical precision. We partner exclusively with clinics where the procedure is overseen by specialized dermatologists or plastic surgeons, ensuring that the extraction and implantation phases meet the highest medical standards.
    `,
  },
  {
    slug: 'cardiac',
    name: 'Cardiac Care',
    icon: HeartPulse,
    shortBlurb: 'Bypass surgery, valve replacement, and angioplasty.',
    heroImage:
      'https://images.unsplash.com/photo-1530497610245-94d3c16cda28?q=80&w=2864&auto=format&fit=crop',
    illustrativePriceFrom: '$12,000',
    illustrativeSavingsPct: '80%',
    destinations: ['India', 'Thailand', 'Spain'],
    averageProcedureTime: '3 to 6 hours',
    stayRequired: '21 to 30 days to ensure stability for air travel',
    insuranceDetails:
      'Rarely covered by standard domestic insurance unless pre-authorized through a specific global health plan. Patients typically self-fund or utilize specialized medical financing.',
    risks: [
      'Bleeding or infection',
      'Arrhythmias (irregular heartbeats)',
      'Stroke or heart attack during or after surgery',
      'Complications from prolonged anesthesia',
    ],
    priceComparison: [
      { destination: 'United States', price: '$100,000 - $200,000 (CABG)' },
      { destination: 'India', price: '$8,000 - $12,000 (CABG)' },
      { destination: 'Thailand', price: '$20,000 - $30,000 (CABG)' },
      { destination: 'Spain', price: '$25,000 - $35,000 (CABG)' },
    ],
    content: `
      Cardiac care is one of the most critical and complex specialties in medicine. The decision to travel for heart surgery, such as coronary artery bypass grafting (CABG), valve repair or replacement, or complex angioplasty, requires absolute confidence in the medical facility. Leading international hospitals have built dedicated heart institutes staffed by multidisciplinary teams capable of handling the most challenging cardiovascular cases.

      This option is typically considered by uninsured or underinsured patients who require life-saving or life-improving cardiac interventions but cannot afford the exorbitant costs in the United States. In some cases, patients from countries with socialized medicine choose to travel to bypass lengthy waitlists for non-emergency cardiac procedures. The savings realized can be substantial, often amounting to tens of thousands of dollars.

      The patient journey for cardiac care is highly structured. Extensive medical records, including angiograms and echocardiograms, are reviewed by the international surgical team prior to travel. Upon arrival, the patient is admitted for a thorough cardiac workup. Post-surgery, patients spend several days in a specialized cardiac intensive care unit before stepping down to a regular ward. The total stay in the destination country is usually several weeks to ensure the patient is stable enough for air travel.
    `,
  },
  {
    slug: 'fertility',
    name: 'Fertility (IVF)',
    icon: Baby,
    shortBlurb:
      'IVF cycles, egg freezing, and comprehensive fertility treatments.',
    heroImage:
      'https://images.unsplash.com/photo-1583912265927-8c56d5926230?q=80&w=2940&auto=format&fit=crop',
    illustrativePriceFrom: '$4,500',
    illustrativeSavingsPct: '65%',
    destinations: ['Spain', 'Mexico', 'Czech Republic', 'Greece'],
    averageProcedureTime:
      'Outpatient procedures (retrieval/transfer take under 1 hour)',
    stayRequired: '7 to 14 days per cycle phase',
    insuranceDetails:
      'Often an out-of-pocket expense as many domestic plans exclude fertility treatments. Traveling allows patients to afford multiple cycles for the price of one domestic cycle.',
    risks: [
      'Ovarian Hyperstimulation Syndrome (OHSS)',
      'Multiple pregnancies (twins or more)',
      'Ectopic pregnancy',
      'Emotional and psychological stress',
    ],
    priceComparison: [
      { destination: 'United States', price: '$15,000 - $25,000 (per cycle)' },
      { destination: 'Spain', price: '$5,000 - $8,000 (per cycle)' },
      { destination: 'Czech Republic', price: '$3,500 - $5,500 (per cycle)' },
      { destination: 'Mexico', price: '$6,000 - $9,000 (per cycle)' },
    ],
    content: `
      Fertility treatments, particularly In Vitro Fertilization (IVF), represent a significant emotional and financial investment. Many international clinics offer success rates that are comparable to, or even exceed, those of domestic clinics, while providing care at a fraction of the cost. These clinics utilize advanced reproductive technologies, including ICSI, preimplantation genetic testing (PGT), and comprehensive donor programs.

      Couples and individuals struggling with infertility often find themselves priced out of multiple IVF cycles in their home countries. Medical travel allows patients to afford the multiple cycles that are sometimes necessary to achieve a successful pregnancy. Additionally, some patients travel to access specific treatments or donor profiles that may be restricted or unavailable in their local jurisdictions.

      The international IVF process is designed to be as seamless as possible. Patients typically begin their ovarian stimulation protocols in their home country under the guidance of their local gynecologist, coordinating closely with the international clinic. Travel is usually required only for the egg retrieval and embryo transfer procedures, minimizing the time spent away from home. The clinics provide extensive support throughout the process, recognizing the emotional sensitivity of fertility treatments.
    `,
  },
  {
    slug: 'ophthalmology',
    name: 'Ophthalmology',
    icon: Eye,
    shortBlurb: 'LASIK, cataract surgery, and advanced eye care.',
    heroImage:
      'https://images.unsplash.com/photo-1580281658626-ee379eb74988?q=80&w=2825&auto=format&fit=crop',
    illustrativePriceFrom: '$1,500',
    illustrativeSavingsPct: '60%',
    destinations: ['Spain', 'Turkey', 'India'],
    averageProcedureTime: '30 minutes to 2 hours',
    stayRequired: '3 to 7 days for post-op checkups',
    insuranceDetails:
      'LASIK is generally considered elective and not covered. Cataract surgery may be covered domestically, but patients travel to access premium multifocal lenses without the massive out-of-pocket upgrades required at home.',
    risks: [
      'Dry eyes or visual disturbances (halos/glare)',
      'Infection or inflammation',
      'Under-correction or over-correction',
      'Flap complications (in LASIK)',
    ],
    priceComparison: [
      {
        destination: 'United States',
        price: '$4,000 - $6,000 (LASIK both eyes)',
      },
      { destination: 'Turkey', price: '$1,500 - $2,500 (LASIK both eyes)' },
      { destination: 'Spain', price: '$2,000 - $3,500 (LASIK both eyes)' },
      { destination: 'India', price: '$1,000 - $2,000 (LASIK both eyes)' },
    ],
    content: `
      Ophthalmology and advanced eye care procedures are highly sought after by international patients seeking to correct their vision or address degenerative conditions. Procedures such as LASIK, PRK, and refractive lens exchange are performed using the exact same advanced laser platforms (such as Zeiss or Alcon) found in top Western clinics, but at a significantly reduced cost.

      For older patients, cataract surgery with premium intraocular lenses (IOLs) is a major draw. While basic cataract surgery is often covered by domestic insurance, upgrading to multifocal or toric lenses to eliminate the need for glasses can cost thousands of dollars out-of-pocket. International clinics offer these premium lenses at a fraction of the upgrade fee.

      The recovery from most ophthalmologic procedures is rapid, allowing patients to enjoy their destination shortly after surgery. We partner with specialized eye institutes that maintain rigorous sterilization protocols and employ surgeons who have performed thousands of successful refractive procedures.
    `,
  },
  {
    slug: 'oncology',
    name: 'Oncology & Terminal Care',
    icon: Activity,
    shortBlurb:
      'Advanced cancer treatments, palliative care, and long-stay settlement.',
    heroImage:
      'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?q=80&w=2906&auto=format&fit=crop',
    illustrativePriceFrom: '$25,000',
    illustrativeSavingsPct: '50%',
    destinations: ['United Arab Emirates', 'India', 'Spain'],
    averageProcedureTime: 'Varies widely (weeks to months)',
    stayRequired: 'Long-term stay or permanent settlement',
    insuranceDetails:
      'Often funded through employer self-funded insurance options or private wealth. Some international health plans cover advanced treatments abroad when domestic wait times are prohibitive.',
    risks: [
      'Complications from advanced treatments (chemotherapy, radiation)',
      'Emotional and psychological stress of long-term care abroad',
      'Logistical challenges of international relocation',
    ],
    priceComparison: [
      { destination: 'United States', price: '$100,000+ (Comprehensive Care)' },
      {
        destination: 'United Arab Emirates',
        price: '$50,000 - $80,000 (Premium Care)',
      },
      { destination: 'India', price: '$25,000 - $40,000 (Comprehensive Care)' },
      { destination: 'Spain', price: '$40,000 - $60,000 (Comprehensive Care)' },
    ],
    content: `
      Oncology and terminal care represent a specialized and deeply compassionate segment of medical travel. Patients facing complex cancer diagnoses or requiring long-term palliative care often seek international options to bypass massive wait times in countries like Canada, the UK, and Australia. Immediate access to advanced treatments, such as targeted therapies, immunotherapy, and robotic surgery, can be critical.

      Many patients and their families choose to relocate or settle in medical tourism hubs that offer comprehensive care ecosystems. Destinations like the UAE (Dubai) offer retirement and investment visas, allowing wealthy individuals to invest in property and secure long-term residency. These cities feature clusters of world-class hospitals, 24/7 medical care, assisted living services, and premium amenities tailored to long-term recovery and comfort.

      The decision to seek oncology or terminal care abroad is often supported by employer self-funded insurance options or private wealth. Facilities in our network provide multidisciplinary tumor boards, holistic support services, and highly personalized care plans, ensuring that patients receive the highest standard of medical and emotional support during their journey.
    `,
  },
]
